import type { TimeSlot } from '@/types';
import './TimeSlotGrid.css';

interface TimeSlotGridProps {
  slots: TimeSlot[];
  selectedTime: string;
  onSelectTime: (time: string) => void;
  loading: boolean;
  selectedDate: string;
}

const MONTHS_SHORT = [
  'ene', 'feb', 'mar', 'abr', 'may', 'jun',
  'jul', 'ago', 'sep', 'oct', 'nov', 'dic',
];

function formatDateLabel(dateStr: string): string {
  if (!dateStr) return '';
  const [year, month, day] = dateStr.split('-').map(Number);
  const d = new Date(year, month - 1, day);
  const weekday = d.toLocaleDateString('es-ES', { weekday: 'long' });
  return `${weekday.charAt(0).toUpperCase() + weekday.slice(1)}, ${day} ${MONTHS_SHORT[month - 1]} ${year}`;
}

export default function TimeSlotGrid({
  slots,
  selectedTime,
  onSelectTime,
  loading,
  selectedDate,
}: TimeSlotGridProps) {
  const morningSlots = slots.filter((s) => {
    const h = parseInt(s.value.split(':')[0], 10);
    return h < 13;
  });
  const afternoonSlots = slots.filter((s) => {
    const h = parseInt(s.value.split(':')[0], 10);
    return h >= 13 && h < 19;
  });
  const nightSlots = slots.filter((s) => {
    const h = parseInt(s.value.split(':')[0], 10);
    return h >= 19;
  });

  if (!selectedDate) {
    return (
      <div className="timeslot-empty">
        <div className="timeslot-empty-icon">
          <i className="ri-calendar-line" />
        </div>
        <p className="timeslot-empty-text">
          Selecciona una fecha en el<br />calendario para ver los horarios
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="space-y-5">
        <div className="timeslot-skeleton-label" />
        <div className="timeslot-period-grid">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="timeslot-skeleton-btn" />
          ))}
        </div>
        <div className="timeslot-skeleton-label mt-4" />
        <div className="timeslot-period-grid">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="timeslot-skeleton-btn" />
          ))}
        </div>
      </div>
    );
  }

  const availableCount = slots.filter((s) => s.available).length;

  return (
    <div className="timeslot-grid-root">
      {/* Date label */}
      <div className="timeslot-date-header">
        <p className="timeslot-date-label">{formatDateLabel(selectedDate)}</p>
        <span className="timeslot-count">
          {availableCount} disponible{availableCount !== 1 ? 's' : ''}
        </span>
      </div>

      {availableCount === 0 ? (
        <div className="timeslot-no-avail">
          <div className="timeslot-no-avail-icon">
            <i className="ri-calendar-close-line" />
          </div>
          <p className="timeslot-no-avail-text">
            No hay horarios disponibles<br />para este día
          </p>
        </div>
      ) : (
        <>
          {morningSlots.length > 0 && (
            <div className="timeslot-period">
              <div className="timeslot-period-header">
                <i className="ri-sun-line" />
                <span className="timeslot-period-label">Mañana</span>
              </div>
              <div className="timeslot-period-grid">
                {morningSlots.map((slot) => (
                  <SlotButton key={slot.value} slot={slot} selected={selectedTime === slot.value} onSelect={onSelectTime} />
                ))}
              </div>
            </div>
          )}

          {afternoonSlots.length > 0 && (
            <div className="timeslot-period">
              <div className="timeslot-period-header">
                <i className="ri-sun-foggy-line" />
                <span className="timeslot-period-label">Tarde</span>
              </div>
              <div className="timeslot-period-grid">
                {afternoonSlots.map((slot) => (
                  <SlotButton key={slot.value} slot={slot} selected={selectedTime === slot.value} onSelect={onSelectTime} />
                ))}
              </div>
            </div>
          )}

          {nightSlots.length > 0 && (
            <div className="timeslot-period">
              <div className="timeslot-period-header timeslot-period-header--night">
                <i className="ri-moon-line" />
                <span className="timeslot-period-label timeslot-period-label--night">Noche</span>
              </div>
              <div className="timeslot-period-grid">
                {nightSlots.map((slot) => (
                  <SlotButton key={slot.value} slot={slot} selected={selectedTime === slot.value} onSelect={onSelectTime} night />
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

interface SlotButtonProps {
  slot: TimeSlot;
  selected: boolean;
  onSelect: (time: string) => void;
  night?: boolean;
}

function SlotButton({ slot, selected, onSelect, night }: SlotButtonProps) {
  if (!slot.available) return null;

  return (
    <button
      type="button"
      onClick={() => onSelect(slot.value)}
      className={`slot-btn ${selected ? 'slot-btn--selected' : ''}`}
    >
      {slot.label}
    </button>
  );
}